import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import './OtherPages.css';

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.55, ease: [0.4, 0, 0.2, 1] },
  }),
};

// ── Split desarrollo text into paragraphs ──────────────────────
function Paragraphs({ text }) {
  if (!text) return null;
  return (
    <div className="paragraphs">
      {text.split(/\n\n+/).map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CRÓNICAS
   ═══════════════════════════════════════════════════════════════ */
const ERA_ORDER = [
  'Era Cero',
  'Hace ~10.000',
  'Hace ~5.000',
  'Hace ~2.000',
  'Año 0',
  'Hace ~500',
  'Año 2.247',
  'Post-Game',
];

function eraKey(linea = '') {
  for (const e of ERA_ORDER) if (linea.includes(e)) return e;
  return linea;
}

const ESTADO_STYLE = {
  'completado':    { bg: 'rgba(20,83,45,0.7)',  text: '#86efac', dot: '#4ade80' },
  'en desarrollo': { bg: 'rgba(113,63,18,0.7)', text: '#fcd34d', dot: '#fbbf24' },
  'aprobado':      { bg: 'rgba(20,83,45,0.7)',  text: '#86efac', dot: '#4ade80' },
};
function estadoStyle(e = '') {
  return ESTADO_STYLE[e.toLowerCase()] || { bg: 'rgba(30,26,20,0.7)', text: '#a8a29e', dot: '#78716c' };
}

export function Lore() {
  const { data: lista, loading } = useApi('/api/lore');
  const [expanded, setExpanded]   = useState(null);
  const [filter,   setFilter]     = useState('Todos');

  const eras = useMemo(() => {
    if (!lista) return [];
    const set = new Set(lista.map(l => eraKey(l.lineaTemporal || '')));
    return ['Todos', ...ERA_ORDER.filter(e => set.has(e)), ...[...set].filter(e => !ERA_ORDER.includes(e))];
  }, [lista]);

  const filtered = useMemo(() => {
    if (!lista) return [];
    if (filter === 'Todos') return lista;
    return lista.filter(l => eraKey(l.lineaTemporal || '') === filter);
  }, [lista, filter]);

  return (
    <div className="page other-page">
      <div className="container">

        {/* Header */}
        <motion.div className="page-header" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="page-header-eyebrow">◈ INSTITUTO MIRAVEL — ARCHIVO HISTÓRICO</div>
          <h1>Crónicas de Veltara</h1>
          <p className="page-subtitle">Registros narrativos catalogados desde la Era Cero hasta el presente</p>
        </motion.div>

        {/* Era filter */}
        {eras.length > 1 && (
          <motion.div className="filter-bar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            {eras.map(e => (
              <button
                key={e}
                className={`filter-btn ${filter === e ? 'active' : ''}`}
                onClick={() => { setFilter(e); setExpanded(null); }}
              >
                {e}
              </button>
            ))}
          </motion.div>
        )}

        {loading && <div className="loading-rune">Descifrando los archivos...</div>}

        {/* Timeline list */}
        <div className="lore-timeline-list">
          {filtered.map((l, i) => {
            const es   = estadoStyle(l.estado);
            const open = expanded === l._id;
            return (
              <motion.div
                key={l._id}
                className={`lore-entry ${open ? 'open' : ''}`}
                variants={fadeUp} initial="hidden" whileInView="visible"
                viewport={{ once: true }} custom={i}
              >
                {/* Timeline spine */}
                <div className="lore-spine">
                  <div className="lore-spine-dot" style={{ background: es.dot }} />
                  <div className="lore-spine-line" />
                </div>

                {/* Card */}
                <div className="lore-card" onClick={() => setExpanded(open ? null : l._id)}>

                  <div className="lore-card-top">
                    {/* Meta row */}
                    <div className="lore-meta">
                      <span className="lore-timeline-label">{l.lineaTemporal}</span>
                      <span className="lore-estado-badge" style={{ background: es.bg, color: es.text }}>
                        <span className="lore-estado-dot" style={{ background: es.dot }} />
                        {l.estado}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="lore-entry-title">{l.titulo}</h2>

                    {/* Sujetos */}
                    {l.sujetos && (
                      <div className="lore-sujetos-row">
                        {String(l.sujetos).split(',').map(s => s.trim()).filter(Boolean).map(s => (
                          <span key={s} className="lore-sujeto-tag">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Expandable content */}
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        className="lore-card-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="lore-card-body-inner">
                          <Paragraphs text={l.desarrollo} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="lore-toggle-row">
                    <span className="lore-toggle">{open ? '▲ Colapsar archivo' : '▼ Leer archivo completo'}</span>
                    <span className="lore-file-id">REG-{String(i + 1).padStart(3, '0')}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {!loading && (!lista || lista.length === 0) && (
          <div className="empty-state">
            <span className="empty-icon">📜</span>
            Los archivos narrativos están vacíos.
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PERSONAJES
   ═══════════════════════════════════════════════════════════════ */

// Extraer categoría corta de la clasificación completa
function roleCategory(clasif = '') {
  const c = clasif.toLowerCase();
  if (c.includes('protagonista'))                        return 'Protagonista';
  if (c.includes('rival'))                               return 'Rival';
  if (c.includes('líder') || c.includes('lider'))       return 'Líder';
  if (c.includes('orden') || c.includes('comandante') ||
      c.includes('lugarteniente') || c.includes('gran maestra')) return 'Orden';
  return 'NPC';
}

const ROLE_CONFIG = {
  'Protagonista': { accent: '#7dd3fc', bg: 'rgba(14,116,144,0.18)',  label: '#7dd3fc' },
  'Rival':        { accent: '#c084fc', bg: 'rgba(107,33,168,0.18)',  label: '#c084fc' },
  'Líder':        { accent: '#fbbf24', bg: 'rgba(120,53,15,0.18)',   label: '#fbbf24' },
  'Orden':        { accent: '#f87171', bg: 'rgba(127,29,29,0.18)',   label: '#f87171' },
  'NPC':          { accent: '#a8a29e', bg: 'rgba(41,37,36,0.18)',    label: '#a8a29e' },
};

const FILTERS_PERSONAJES = ['Todos', 'Protagonista', 'Rival', 'Líder', 'Orden', 'NPC'];

export function Personajes() {
  const { data: lista, loading } = useApi('/api/personajes');
  const [filter,   setFilter]   = useState('Todos');
  const [expanded, setExpanded] = useState(null);

  const filtered = useMemo(() => {
    if (!lista) return [];
    if (filter === 'Todos') return lista;
    return lista.filter(p => roleCategory(p.clasificacion) === filter);
  }, [lista, filter]);

  return (
    <div className="page other-page">
      <div className="container">

        {/* Header */}
        <motion.div className="page-header" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="page-header-eyebrow">◈ INSTITUTO MIRAVEL — REGISTRO DE SUJETOS</div>
          <h1>Registro de Sujetos</h1>
          <p className="page-subtitle">Individuos de interés catalogados por el Instituto Miravel</p>
        </motion.div>

        {/* Role filter */}
        <motion.div className="filter-bar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          {FILTERS_PERSONAJES.map(f => (
            <button
              key={f}
              className={`filter-btn ${filter === f ? 'active' : ''}`}
              style={filter === f && f !== 'Todos' ? {
                borderColor: ROLE_CONFIG[f]?.accent,
                color:       ROLE_CONFIG[f]?.accent,
                background:  ROLE_CONFIG[f]?.bg,
              } : {}}
              onClick={() => { setFilter(f); setExpanded(null); }}
            >
              {f}
            </button>
          ))}
        </motion.div>

        {loading && <div className="loading-rune">Consultando el archivo de sujetos...</div>}

        <div className="personajes-grid-v2">
          {filtered.map((p, i) => {
            const role = roleCategory(p.clasificacion);
            const cfg  = ROLE_CONFIG[role];
            const open = expanded === p._id;

            return (
              <motion.div
                key={p._id}
                className={`sujeto-card ${open ? 'open' : ''}`}
                style={{ '--accent': cfg.accent, '--accent-bg': cfg.bg }}
                variants={fadeUp} initial="hidden" whileInView="visible"
                viewport={{ once: true }} custom={i}
              >
                {/* Accent bar */}
                <div className="sujeto-accent-bar" style={{ background: cfg.accent }} />

                <div className="sujeto-body">
                  {/* Portrait placeholder */}
                  <div className="sujeto-portrait" style={{ borderColor: cfg.accent + '55' }}>
                    {p.imagen
                      ? <img src={p.imagen} alt={p.nombre} />
                      : <span className="sujeto-portrait-glyph" style={{ color: cfg.accent }}>◈</span>
                    }
                  </div>

                  {/* Identity */}
                  <div className="sujeto-identity">
                    <div className="sujeto-role-badge" style={{ color: cfg.label, background: cfg.bg, borderColor: cfg.accent + '44' }}>
                      {role}
                    </div>
                    <h3 className="sujeto-nombre">{p.nombre}</h3>
                    <p className="sujeto-clasif">{p.clasificacion}</p>
                  </div>

                  {/* Rasgos */}
                  <p className="sujeto-rasgos">{p.rasgos}</p>

                  {/* Motivación */}
                  <div className="sujeto-section">
                    <span className="sujeto-label" style={{ color: cfg.accent }}>Motivación</span>
                    <p className="sujeto-text">{p.motivacion}</p>
                  </div>

                  {/* Expandable: historia + equipo */}
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                      >
                        {p.historia && (
                          <div className="sujeto-section sujeto-historia">
                            <span className="sujeto-label" style={{ color: cfg.accent }}>Historia</span>
                            <Paragraphs text={p.historia} />
                          </div>
                        )}

                        {p.equipo?.length > 0 && (
                          <div className="sujeto-section">
                            <span className="sujeto-label" style={{ color: cfg.accent }}>Equipo Pokémon</span>
                            <div className="sujeto-equipo-list">
                              {p.equipo.map((e, j) => (
                                <span key={j} className="sujeto-equipo-chip">{e}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Toggle */}
                  <button
                    className="sujeto-toggle"
                    style={{ color: cfg.accent + 'bb' }}
                    onClick={() => setExpanded(open ? null : p._id)}
                  >
                    {open ? '▲ Colapsar ficha' : '▼ Ver ficha completa'}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {!loading && filtered.length === 0 && lista?.length > 0 && (
          <div className="empty-state">
            <span className="empty-icon">🔍</span>
            Ningún sujeto coincide con este filtro.
          </div>
        )}
        {!loading && (!lista || lista.length === 0) && (
          <div className="empty-state">
            <span className="empty-icon">⚔️</span>
            Ningún sujeto ha sido catalogado aún.
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAPA
   ═══════════════════════════════════════════════════════════════ */
export function Mapa() {
  const zonas = [
    { nombre: 'Montaña Sepulcro', tipo: 'Montaña', pos: { top: '10%', left: '40%' }, icon: '🏔️', desc: 'Donde Valdros duerme hace diez milenios.' },
    { nombre: 'Aldea Cerul',      tipo: 'Ciudad',   pos: { top: '75%', left: '45%' }, icon: '🏘️', desc: 'Punto de inicio. Pueblo costero del sur.' },
    { nombre: 'Ciudad Miravel',   tipo: 'Ciudad',   pos: { top: '45%', left: '50%' }, icon: '🏰', desc: 'Corazón del Instituto Miravel.' },
    { nombre: 'Bosque Umbra',     tipo: 'Zona',     pos: { top: '50%', left: '15%' }, icon: '🌲', desc: 'Impenetrable. Guardianes Umbra lo custodian.' },
    { nombre: 'Calderas de Ignan',tipo: 'Zona',     pos: { top: '25%', left: '70%' }, icon: '🌋', desc: 'Terreno volcánico. Hermandad Ignan.' },
    { nombre: 'Lago Espejo',      tipo: 'Punto',    pos: { top: '55%', left: '55%' }, icon: '💧', desc: 'Morada original de Miravel.' },
    { nombre: 'Puerto Áureo',     tipo: 'Ciudad',   pos: { top: '60%', left: '80%' }, icon: '⚓', desc: 'Costa del Mar Áureo. Torneo callejero.' },
    { nombre: 'Ferrante',         tipo: 'Ciudad',   pos: { top: '30%', left: '30%' }, icon: '⛏️', desc: 'Ciudad minera. Clan Ferruza.' },
  ];

  return (
    <div className="page other-page">
      <div className="container">
        <motion.div className="page-header" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1>Cartografía de Veltara</h1>
          <p className="page-subtitle">Mapa preliminar de la región — en desarrollo</p>
        </motion.div>

        <div className="mapa-container">
          <div className="mapa-grid-bg" />
          {zonas.map((z, i) => (
            <motion.div
              key={z.nombre}
              className="mapa-zona"
              style={z.pos}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 + 0.3, type: 'spring', stiffness: 260, damping: 20 }}
              title={z.desc}
            >
              <div className="mapa-zona-icon">{z.icon}</div>
              <div className="mapa-zona-nombre">{z.nombre}</div>
            </motion.div>
          ))}
          <div className="mapa-watermark">MAPA PRELIMINAR — INSTITUTO MIRAVEL</div>
        </div>

        <div className="mapa-legend">
          {zonas.map((z, i) => (
            <motion.div
              key={z.nombre}
              className="crystal-card mapa-legend-card"
              variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} custom={i}
            >
              <span className="mapa-legend-icon">{z.icon}</span>
              <div>
                <h3 className="mapa-legend-nombre">{z.nombre}</h3>
                <p className="mapa-legend-desc">{z.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
